import axios from 'axios'
import { trackBriefPlacedConversion } from '../utils/adsConversion'

// Create axios instance - uses HttpOnly cookies for auth
export const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production'
    ? 'https://ugc4you.nl/api'
    : 'http://localhost:5000/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with every request
})

// Voorkomt meerdere gelijktijdige sessie-checks/redirects bij een burst van 401's.
let sessionCheckInFlight = false

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      const isAuthCheck = url.includes('/auth/me')
      const publicPaths = ['/', '/login', '/register', '/forgot-password', '/projects', '/creators', '/companies', '/opdracht-starten', '/payment-success', '/payment-cancel', '/algemene-voorwaarden', '/privacy-policy']
      const isPublicPage = publicPaths.includes(window.location.pathname) ||
                           window.location.pathname.startsWith('/admin') ||
                           window.location.pathname.startsWith('/creators/') ||
                           window.location.pathname.startsWith('/companies/') ||
                           window.location.pathname.startsWith('/projects/')

      // Een enkele 401 betekent NIET automatisch dat je sessie dood is — het kan een
      // transiente hapering zijn (backend net herstart, korte netwerkfout). Vroeger
      // reloadde élke 401 meteen naar /login, wat ingevulde formulieren (bijv. "Nieuwe
      // opdracht plaatsen") wiste. We verifiëren nu eerst via /auth/me en loggen alléén
      // uit als die óók 401 geeft — dus alleen bij een écht verlopen sessie. De
      // oorspronkelijke fout propageert direct (niet-blokkerend) naar de aanroeper.
      if (!isAuthCheck && !isPublicPage && !sessionCheckInFlight) {
        sessionCheckInFlight = true
        api.get('/auth/me')
          .catch((authErr) => { if (authErr.response?.status === 401) window.location.href = '/login' })
          .finally(() => { sessionCheckInFlight = false })
      }
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { currentPassword, newPassword }),
}

// Users API
export const usersAPI = {
  getCreators: (params) => api.get('/users/creators', { params }),
  getCreator: (id) => api.get(`/users/creators/${id}`),
  getCompanies: (params) => api.get('/users/companies', { params }),
  getCompany: (id) => api.get(`/users/companies/${id}`),
  updateProfile: (data) => api.put('/users/me/profile', data),
  addPortfolioItem: (data) => api.post('/users/me/portfolio', data),
  updatePortfolioItem: (itemId, data) => api.put(`/users/me/portfolio/${itemId}`, data),
  removePortfolioItem: (itemId) => api.delete(`/users/me/portfolio/${itemId}`),
  addMoodboardItem: (data) => api.post('/users/me/moodboard', data),
  updateMoodboardItem: (itemId, data) => api.put(`/users/me/moodboard/${itemId}`, data),
  removeMoodboardItem: (itemId) => api.delete(`/users/me/moodboard/${itemId}`),
  updateSocialMedia: (data) => api.put('/users/me/social-media', data),
  // Stripe Connect endpoints
  getStripeStatus: () => api.get('/users/me/stripe/status'),
  createStripeConnectAccount: () => api.post('/users/me/stripe/connect'),
  refreshStripeStatus: () => api.post('/users/me/stripe/refresh'),
}

// Projects API
export const projectsAPI = {
  getProjects: (params) => {
    // Filter out empty values from params
    const cleanParams = {}
    for (const key in params) {
      if (params[key] !== '' && params[key] !== undefined && params[key] !== null) {
        cleanParams[key] = params[key]
      }
    }
    return api.get('/projects', { params: cleanParams })
  },
  getProject: (id) => api.get(`/projects/${id}`),
  verlengProject: (id, dagen) => api.post(`/projects/${id}/verleng`, { dagen }),
  // Meta 'Lead' bij elke geslaagde plaatsing — beide paden (wizard + create-pagina) komen hier langs
  createProject: (data) => api.post('/projects', data).then((res) => { if (!data?.isDraft) trackBriefPlacedConversion(res?.data?.project?.id); return res }),
  updateProject: (id, data) => api.put(`/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/projects/${id}`),
  getMyProjects: (params) => api.get('/projects/my', { params }),
  getDraft: () => api.get('/projects/draft'),
  saveDraft: (data) => api.put('/projects/draft', { data }),
  deleteDraft: () => api.delete('/projects/draft'),
  undoAcceptProposal: (id) => api.post(`/projects/${id}/undo-accept`),
  getMyActiveProjects: (params) => api.get('/projects/my-active', { params }),
  submitProject: (id) => api.post(`/projects/${id}/submit`),
  reviewDeliverable: (projectId, data) => api.post(`/projects/${projectId}/review-deliverable`, data),
  approveProject: (projectId, data) => api.post(`/projects/${projectId}/approve`, data),
  requestRevision: (projectId, data) => api.post(`/projects/${projectId}/request-revision`, data),
  // Package booking
  bookPackage: (data) => api.post('/projects/book-package', data),
  // Shipping / Track & Trace
  submitShipping: (id, data) => api.post(`/projects/${id}/shipping`, data),
  updateShipping: (id, data) => api.put(`/projects/${id}/shipping`, data),
  confirmReceipt: (id) => api.post(`/projects/${id}/confirm-receipt`),
  // Retourzending fysiek product (action: 'shipped' | 'received' | 'instructions')
  updateProductReturn: (id, data) => api.post(`/projects/${id}/product-return`, data),
}

// Proposals API
export const proposalsAPI = {
  submitProposal: (projectId, data) => api.post(`/proposals/project/${projectId}`, data),
  getProjectProposals: (projectId, params) => 
    api.get(`/proposals/project/${projectId}`, { params }),
  getMyProposals: (params) => api.get('/proposals/my', { params }),
  acceptProposal: (proposalId) => api.post(`/proposals/accept/${proposalId}`),
  rejectProposal: (proposalId, data) => api.post(`/proposals/reject/${proposalId}`, data),
}

// Invitations API
export const invitationsAPI = {
  create: (data) => api.post('/invitations', data),
  getMy: (params) => api.get('/invitations/my', { params }),
  getSent: () => api.get('/invitations/sent'),
  getPendingCount: () => api.get('/invitations/count'),
  accept: (id) => api.post(`/invitations/${id}/accept`),
  decline: (id, data) => api.post(`/invitations/${id}/decline`, data),
}

// Upload API - longer timeouts for file uploads
export const uploadAPI = {
  uploadImage: (file, onProgress) => {
    const formData = new FormData()
    formData.append('image', file)
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // 60s for images
      onUploadProgress: onProgress
    })
  },
  uploadVideo: (file, onProgress) => {
    const formData = new FormData()
    formData.append('video', file)
    return api.post('/upload/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 min for videos
      onUploadProgress: onProgress
    })
  },
  uploadProfilePicture: (file, onProgress) => {
    const formData = new FormData()
    formData.append('profilePicture', file)
    return api.post('/upload/profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress: onProgress
    })
  },
  deleteFile: (publicId, resourceType = 'image') =>
    api.delete(`/upload/${publicId}?resourceType=${resourceType}`),
  uploadDeliverable: (projectId, file, metadata, onProgress) => {
    const formData = new FormData()
    formData.append('deliverable', file)
    formData.append('title', metadata.title)
    formData.append('description', metadata.description || '')
    formData.append('type', metadata.type || 'deliverable')
    return api.post(`/upload/deliverable/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 min for deliverables
      onUploadProgress: onProgress
    })
  },
  
  // Deliverable management
  getDeliverables: (projectId) => api.get(`/upload/deliverables/${projectId}`),
  previewFile: (projectId, fileId) => api.get(`/upload/preview/${projectId}/${fileId}`, { responseType: 'blob', timeout: 90000 }),
  downloadFile: (projectId, fileId) => api.get(`/upload/download/${projectId}/${fileId}`, { responseType: 'blob' }),
}

// Conversations API
export const conversationsAPI = {
  getConversations: (params) => api.get('/conversations', { params }),
  getConversation: (id) => api.get(`/conversations/${id}`),
  createConversation: (data) => api.post('/conversations', data),
  sendMessage: (conversationId, data) => api.post(`/conversations/${conversationId}/messages`, data),
  deleteConversation: (conversationId) => api.delete(`/conversations/${conversationId}`),
  getProjectConversation: (projectId) => api.get(`/conversations/project/${projectId}`),
  sendProjectMessage: (projectId, data) => api.post(`/conversations/project/${projectId}/message`, data)
}

// Analytics API
export const analyticsAPI = {
  getOverview: (timeframe) => api.get('/analytics/overview', { params: { timeframe } }),
  getEarnings: () => api.get('/analytics/earnings'),
}

// Invoices API
export const invoicesAPI = {
  getMyInvoices: () => api.get('/invoices/my'),
  getInvoice: (id) => api.get(`/invoices/${id}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  getProjectInvoices: (projectId) => api.get(`/invoices/project/${projectId}`),
  getSummary: () => api.get('/invoices/summary'),
}

// Billing API
export const billingAPI = {
  getBilling: () => api.get('/users/me/billing'),
  updateBilling: (data) => api.put('/users/me/billing', data),
}

// Stripe Connect API
export const stripeConnectAPI = {
  getStatus: () => api.get('/stripe-connect/status'),
  createAccount: () => api.post('/stripe-connect/create-account'),
  getOnboardingLink: () => api.post('/stripe-connect/onboarding-link'),
  getDashboardLink: () => api.post('/stripe-connect/dashboard-link'),
  disconnect: () => api.delete('/stripe-connect/disconnect'),
  getBalance: () => api.get('/stripe-connect/balance'),
  getPayouts: () => api.get('/stripe-connect/payouts'),
}

// Payments API
export const paymentsAPI = {
  initializePayment: (projectId) => api.post(`/payments/projects/${projectId}/initialize`),
  processDepositPayment: (projectId, data) => api.post(`/payments/projects/${projectId}/deposit`, data),
  processFinalPayment: (projectId, data) => api.post(`/payments/projects/${projectId}/final`, data),
  getPaymentStatus: (projectId) => api.get(`/payments/projects/${projectId}/status`),
  getPaymentHistory: (projectId) => api.get(`/payments/projects/${projectId}/history`),
  getCreatorEarnings: () => api.get('/payments/creator/earnings'),
  simulatePayment: (projectId, type) => api.post(`/payments/simulate-payment/${projectId}/${type}`),
  
  // Custom amount negotiation
  proposeAmount: (projectId, amount) => api.post(`/payments/projects/${projectId}/propose-amount`, { amount }),
  confirmAmount: (projectId) => api.post(`/payments/projects/${projectId}/confirm-amount`),
}

// Notifications API
export const notificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
}

export default api

// Web Push (PWA, 2026-09-10)
export const pushAPI = {
  publicKey: () => api.get('/notifications/push/public-key'),
  subscribe: (subscription) => api.post('/notifications/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.post('/notifications/push/unsubscribe', { endpoint }),
  test: () => api.post('/notifications/push/test'),
}
